import styled from '@emotion/styled'
import { useState } from 'react'

import { RenameFundManagerResult, useRenameFundManagerMutation } from '@/api/asset-fund-manager-api'
import {
  FormItem,
  GenericValidationMessages,
  Label,
  ModalHeader,
  SimpleModal,
  Space,
  TextInput,
  Typography
} from '@/components/core-v2'
import Icon from '@/components/core-v2/atoms/Icon'
import IconButton from '@/components/core-v2/molecules/iconButton'

const TENANT_ID = 'absa'

const StyledBody = styled.div`
  padding: 36px;
`

const StyledActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
`

const extractErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object') {
    const { data } = error as { data?: unknown }
    if (typeof data === 'string' && data.length > 0) return data
    if (data && typeof data === 'object') {
      const { message } = data as { message?: unknown; title?: unknown }
      if (typeof message === 'string' && message.length > 0) return message
      const { title } = data as { title?: unknown }
      if (typeof title === 'string' && title.length > 0) return title
    }
  }
  return 'Something went wrong while renaming the fund manager. Please try again.'
}

export type RenameFundManagerModalProps = {
  onClose: () => void
  onRenamed?: () => void
}

export const RenameFundManagerModal = ({ onClose, onRenamed }: RenameFundManagerModalProps) => {
  const [managementCompanyCode, setManagementCompanyCode] = useState('')
  const [newName, setNewName] = useState('')
  const [result, setResult] = useState<RenameFundManagerResult | undefined>(undefined)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  const [renameFundManager, { isLoading }] = useRenameFundManagerMutation()

  const isValid = managementCompanyCode.trim().length > 0 && newName.trim().length > 0

  const onSubmit = async () => {
    if (!isValid || isLoading) return
    setErrorMessage(undefined)
    try {
      const response = await renameFundManager({
        tenant: TENANT_ID,
        body: {
          managementCompanyCode: managementCompanyCode.trim(),
          newName: newName.trim()
        }
      }).unwrap()
      setResult(response)
      if (onRenamed) onRenamed()
    } catch (error) {
      setErrorMessage(extractErrorMessage(error))
    }
  }

  return (
    <SimpleModal width="520px">
      <ModalHeader onClickClose={onClose}>Rename fund manager</ModalHeader>
      <StyledBody>
        {result ? (
          <>
            <Space isflex alignItems="center" mb={5}>
              <Space mr={2}>
                <Icon name="circleCheck" size="default" />
              </Space>
              <Typography fontSize="13px" letterSpacing="0px" variant="b3">
                {`Renamed ${result.count} fund${result.count === 1 ? '' : 's'}`}
              </Typography>
            </Space>
            <StyledActions>
              <IconButton
                icon={<Icon name="circleCheck" size="default" />}
                iconPosition="left"
                operationType="progress"
                text="Done"
                onClick={onClose}
              />
            </StyledActions>
          </>
        ) : (
          <>
            <Typography fontSize="13px" letterSpacing="0px" variant="b3">
              Rename the fund manager across every fund under a management company code.
            </Typography>
            <Space mt={5}>
              <FormItem>
                <Label>Management company code *</Label>
                <TextInput
                  data-testid="rename-fund-manager-manco-code"
                  value={managementCompanyCode}
                  onChange={e => setManagementCompanyCode(e.target.value)}
                />
              </FormItem>
              <FormItem>
                <Label>New fund manager name *</Label>
                <TextInput
                  data-testid="rename-fund-manager-new-name"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                />
              </FormItem>
            </Space>
            {errorMessage && <GenericValidationMessages errorMessages={[errorMessage]} />}
            <StyledActions>
              <IconButton
                data-testid="rename-fund-manager-submit"
                disabled={!isValid || isLoading}
                icon={<Icon name="save" size="default" />}
                iconPosition="left"
                isLoading={isLoading}
                operationType="progress"
                text="Rename"
                onClick={onSubmit}
              />
              <IconButton
                disabled={isLoading}
                icon={<Icon name="circleX" size="default" />}
                iconPosition="left"
                operationType="cancel"
                text="Cancel"
                onClick={onClose}
              />
            </StyledActions>
          </>
        )}
      </StyledBody>
    </SimpleModal>
  )
}

export default RenameFundManagerModal
